// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every constant the tower runs on, in one file, because a number that lives in the middle of a
// system is a number nobody re-measures.
//
// ── TIME ────────────────────────────────────────────────────────────────────────────────────────
// The atomic unit is a TICK of 0.1 tower-seconds. A shift is 3600 ticks = 360 tower-seconds, shown to
// the player as a four-segment bar rather than a wall clock — a compressed working day, not a
// literal one.
//
// Two things fall out of that choice and both are load-bearing. First, every duration in the game
// (a 45-second wait, a 1.6-second door, an 8-second ride) is many ticks long, so none of them is a
// sub-tick quantity that cannot be measured — an earlier draft ran one game-minute per tick and every
// number in its own spec was smaller than its own resolution. Second, a whole 8-day run is only
// 28,800 ticks, and the per-tick cost is O(cars) rather than O(people) (see sim.ts: walks, waits and
// rides are all closed-form, so the only thing that has to be touched every tick is a moving car).
// That is what lets the balance sim run hundreds of full runs inside a normal test suite.

/** Tower-seconds in one tick. */
export const TICK_S = 0.1;
/** Ticks in one shift. */
export const DAY_TICKS = 3600;

/** Ticks per real second at each speed setting. Index 0 is paused. */
export const SPEEDS = [0, 20, 60, 120] as const;
export const SPEED_LABELS = ['||', '×1', '×3', '×6'] as const;
/** A fresh run starts here — ×3, so a shift is 60 real seconds. */
export const DEFAULT_SPEED = 2;

/**
 * The shift, in four phases. `UP` and `DOWN` are the peaks; `MID` is the hard one, because
 * collective control handles two-way traffic worst — and that is the algorithm's property, not a
 * number somebody tuned. `NIGHT` is near-empty and is the build window.
 */
export const PHASE_UP = 0;
export const PHASE_MID = 1100;
export const PHASE_DOWN = 2000;
export const PHASE_NIGHT = 3000;
export type Phase = 'up' | 'mid' | 'down' | 'night';

export function phaseAt(tick: number): Phase {
  if (tick < PHASE_MID) return 'up';
  if (tick < PHASE_DOWN) return 'mid';
  if (tick < PHASE_NIGHT) return 'down';
  return 'night';
}

// ── GEOMETRY ────────────────────────────────────────────────────────────────────────────────────
//
// The cap is the design. 24 × 12 is the largest tower that fits, whole, on a 375×812 phone at a
// legible 22px per storey — so there is no camera, no zoom, and therefore no zoom level at which a
// control can end up off-screen. Principle #20's favourite hiding place does not exist here.

export const STOREYS = 24;
export const COLS = 12;
/** Storey 0. Everyone enters and leaves the tower here. Never called a "lobby": that word means the
 *  netcode screen in every other game in this fleet. */
export const CONCOURSE = 0;

export const MAX_RISERS = 6;
export const MAX_CARS = 12;
export const MAX_AGENTS = 256;
/** Cars you may own: one, plus one per service room built. */
export const BASE_CAR_SLOTS = 1;

// ── MOVEMENT ────────────────────────────────────────────────────────────────────────────────────

/** Ticks to walk one column along a storey. */
export const WALK_TICKS_PER_COL = 7;
/** Ticks to climb one storey by stairs — nine tower-seconds, which is deliberately worse than any
 *  lift that is actually running, and better than a lift that is not. */
export const STAIR_TICKS_PER_STOREY = 90;

/** Car kinematics, in storeys and tower-seconds. */
export const CAR_LOCAL = { vmax: 2.2, accel: 1.2, cap: 12, cost: 1600, upkeep: 40, doorExtra: 0 } as const;
/**
 * The express car is faster per storey and WORSE per stop (a heavier door cycle), and it only serves
 * the concourse plus storeys at or above the riser's express floor. So it is not "the better car you
 * buy when you can afford it" — it is only correct when its service set is sparse, which turns a
 * purchase into a routing decision.
 */
export const CAR_EXPRESS = { vmax: 3.6, accel: 1.8, cap: 14, cost: 2600, upkeep: 62, doorExtra: 12 } as const;
export type CarSpec = typeof CAR_LOCAL | typeof CAR_EXPRESS;

/** Door cycle: a base, plus time per person crossing the threshold, clamped both ends. */
export const DOOR_BASE_TICKS = 8;
export const DOOR_PER_PERSON_TICKS = 3;
export const DOOR_MIN_TICKS = 10;
export const DOOR_MAX_TICKS = 70;

/** A car idle this long with nothing to do returns to the concourse end of its riser. */
export const PARK_AFTER_TICKS = 200;


// ── BUILDING ────────────────────────────────────────────────────────────────────────────────────

export interface UnitSpec {
  kind: UnitKind;
  name: string;
  /** Columns occupied on its storey. */
  w: number;
  cost: number;
  upkeep: number;
  /** People this unit puts in the tower when let. */
  pop: number;
  /** Daily rent before the storey premium and the satisfaction multiplier. Shops earn footfall. */
  rent: number;
}
export type UnitKind = 'office' | 'flat' | 'shop' | 'service';

export const UNITS: Record<UnitKind, UnitSpec> = {
  office: { kind: 'office', name: 'Office', w: 3, cost: 480, upkeep: 30, pop: 4, rent: 178 },
  flat: { kind: 'flat', name: 'Flat', w: 2, cost: 360, upkeep: 22, pop: 2, rent: 112 },
  shop: { kind: 'shop', name: 'Shop', w: 2, cost: 300, upkeep: 26, pop: 1, rent: 0 },
  service: { kind: 'service', name: 'Service', w: 1, cost: 250, upkeep: 18, pop: 1, rent: 0 },
};
export const UNIT_ORDER: readonly UnitKind[] = ['office', 'flat', 'shop', 'service'];

/** A riser is 2 columns wide on every storey it passes — the lift you need costs you the floors you
 *  wanted, which is the whole tension of the genre and the reason this is not a placement game. */
export const RISER_W = 2;
export const RISER_COST_PER_STOREY = 90;
export const RISER_UPKEEP_PER_STOREY = 6;
export const RISER_MIN_SPAN = 3;

/** Stairs link one storey to the next, one column wide on both. */
export const STAIR_COST = 90;
export const STAIR_UPKEEP = 4;

/** Selling anything back returns this fraction of what it cost. */
export const SELL_REFUND = 0.4;

// ── PEOPLE ──────────────────────────────────────────────────────────────────────────────────────

/** A wait at or under this many tower-seconds costs a tenancy nothing; above it, satisfaction falls. */
export const GOOD_WAIT_S = 55;
/** How much one completed trip can move satisfaction, in either direction. Symmetric on purpose: an
 *  asymmetric version makes satisfaction a one-way ratchet and every choice after day 3 irrelevant. */
export const SAT_PER_TRIP = 0.06;
/** A person who waits this long in one go gives up, walks out, and takes a chunk of mood with them. */
export const GIVE_UP_TICKS = 1500;
export const SAT_PER_GIVEUP = 0.18;
/** A tenancy starts here. */
export const SAT_START = 0.7;
/** Below this at two consecutive night boundaries, the tenancy gives notice. */
export const SAT_NOTICE = 0.35;
/** At or below this the unit is drawn as unhappy, so the player can act before the notice lands. */
export const SAT_WARN = 0.5;

/** Chance a worker makes one errand trip during MID, as a fraction — resolved by hash, not a draw. */
export const ERRAND_SHARE = 0.3;
/** How far a worker will travel for an errand. */
export const ERRAND_REACH = 6;

// ── ECONOMY ─────────────────────────────────────────────────────────────────────────────────────

/** Income per unit of footfall for a shop. */
export const SHOP_PER_FOOTFALL = 2.2;
/** Every storey above the concourse adds this fraction to a unit's rent. High floors pay more and are
 *  hardest to serve — the whole reason a tall tower is a decision rather than an accumulation. */
export const STOREY_PREMIUM = 0.05;
/** Rent actually collected scales with satisfaction between these bounds. An unhappy tenant pays less
 *  BEFORE it leaves, so the income slide starts before the cliff and the cascade is felt, not announced. */
export const RENT_SAT_FLOOR = 0.6;
export const RENT_SAT_CEIL = 1.15;
/** Cash below zero at this many consecutive night boundaries ends the run. */
export const BUST_DAYS = 3;

/** A unit that has been empty this long will take a tenant again — if its storey is actually served. */
export const RELET_AFTER_DAYS = 2;
/** …which means the storey's mean wait over the last shift must be under this, in tower-seconds. */
export const RELET_MAX_WAIT_S = 40;

/** The EWMA that route choice and re-letting both read. */
export const EW_ALPHA = 0.25;
export const EW_MIN_S = 3;
export const EW_MAX_S = 90;
/** What a landing's wait estimate starts at, before anyone has measured it. */
export const EW_INIT_S = 12;
/** How far a landing that saw no traffic at all today drifts back toward that starting estimate. */
export const EW_FORGET = 0.35;

// ── DISPATCH ────────────────────────────────────────────────────────────────────────────────────

/** Estimated ticks added per intermediate stop when ranking cars for a call. */
export const ETD_STOP_PENALTY = 29;
/** Added when the car is already at capacity. */
export const ETD_FULL_PENALTY = 120;
/** Added when the car must finish its current run in the wrong direction first. */
export const ETD_WRONG_WAY_PENALTY = 60;
/** Added when a storey is outside the half of the shaft this car is nominally looking after. */
export const ETD_ZONE_PENALTY = 55;

/**
 * A manual summon that asks for something already about to happen is a WHIFF: the riser's controller
 * is locked out for four tower-seconds. Mashing the screen is punished and precision is the skill —
 * without this, the optimal play is to tap everything constantly, which is not a decision.
 */
// Six tower-seconds was far too generous: in a small tower every car is always about six seconds
// from everywhere, so EVERY summon registered as a whiff and the verb could not be used at all — the
// tutorial's own second step could not be completed. A whiff has to mean "it is literally pulling in",
// which is two and a half seconds.
export const SUMMON_WHIFF_WINDOW_TICKS = 25;
export const SUMMON_LOCKOUT_TICKS = 40;

// ── RATING ──────────────────────────────────────────────────────────────────────────────────────

// A rating built only from satisfaction and occupancy rewards a SMALL, FULL, well-served tower — and
// measured across 40 seeded runs the do-nothing control scored BEST in Ascent on exactly that shape,
// which is a game telling the player not to play it. Size is a third of the score now, so standing
// still is a losing move and growth has to be paid for in service.
export const RATING_SAT_WEIGHT = 0.4;
export const RATING_OCC_WEIGHT = 0.2;
export const RATING_SIZE_WEIGHT = 0.4;
/** The population at which the size term is full marks. */
export const RATING_POP = 150;
